"use client";

import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@garagem/ui/components/alert";
import {
  ACCESS_NOTICE_COOKIE,
  type AccessNoticeCode,
  accessNoticeMessage,
  isAccessNoticeCode,
} from "@garagem/foundation-shared/domain/auth/access-policy/notices";

function readNoticeCookie(): AccessNoticeCode | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${ACCESS_NOTICE_COOKIE}=`));
  if (!entry) return null;
  const value = decodeURIComponent(entry.slice(ACCESS_NOTICE_COOKIE.length + 1));
  return isAccessNoticeCode(value) ? value : null;
}

function clearNoticeCookie(): void {
  document.cookie = `${ACCESS_NOTICE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Explains, on /entrar, why the access policy ended the previous session.
 * Reads the short-lived cookie the auth hook left and clears it, so the
 * message shows once.
 */
export function AccessPolicyNotice() {
  const [code, setCode] = useState<AccessNoticeCode | null>(null);

  useEffect(() => {
    const found = readNoticeCookie();
    if (!found) return;
    setCode(found);
    clearNoticeCookie();
  }, []);

  if (!code) return null;

  return (
    <Alert className="mb-4" role="status">
      <Info className="h-4 w-4" />
      <AlertDescription>{accessNoticeMessage(code)}</AlertDescription>
    </Alert>
  );
}
